import {
  ChillerCircuit,
  Fields,
  FormValues,
  Lookups,
  Problem,
  ResourceName,
  Translate,
} from "./CatalogAdmin.types.tsx";
import { FieldSpec, REFERENCES, resourceSpec } from "./resources.tsx";

export const MANAGE_CATALOG = "catalog.manage";
export const DELETE_CATALOG = "catalog.delete";
export const CENTRIFUGAL = "Centrifugal";
export const MAX_CIRCUITS = 4;
export const COEFFICIENTS = 10;

export const can = (permissions: string[] | undefined, permission: string): boolean =>
  Array.isArray(permissions) && permissions.includes(permission);

export const normalizeModel = (model: string): string => model.replace(/ /g, "");

export interface Totals {
  compressors: number;
  condensers: number;
  fans: number;
  evaporators: number;
}

export const totalsOf = (circuits: ChillerCircuit[]): Totals => ({
  compressors: circuits.reduce((sum, circuit) => sum + (circuit.compressors?.length ?? 0), 0),
  condensers: circuits.reduce((sum, circuit) => sum + (circuit.condensers?.quantity ?? 0), 0),
  fans: circuits.reduce((sum, circuit) => sum + (circuit.fans?.quantity ?? 0), 0),
  evaporators: circuits.filter(
    (circuit) => circuit.evaporator !== null && circuit.evaporator !== undefined
  ).length,
});

export const emptyCircuit = (): ChillerCircuit => ({
  compressors: [],
  condensers: null,
  fans: null,
  evaporator: null,
  coils: null,
});

export interface Coefficients {
  values: number[];
  invalid: string[];
}

const tokensOf = (text: string): string[] =>
  text
    .replace(/[[\]]/g, " ")
    .split(/[\s,;]+/)
    .filter((token) => token !== "");

export const parseCoefficients = (text: string): Coefficients => {
  const tokens = tokensOf(text);

  return {
    values: tokens.map(Number).filter((value) => Number.isFinite(value)),
    invalid: tokens.filter((token) => !Number.isFinite(Number(token))),
  };
};

export const parseVolts = (text: string): number[] | null => {
  const values = tokensOf(text).map(Number);

  return values.every((value) => Number.isInteger(value)) ? values : null;
};

export const missingCompressors = (
  circuits: ChillerCircuit[],
  known: string[],
  technologyName: string | null
): string[] => {
  if (technologyName === CENTRIFUGAL) {
    return [];
  }

  const available = new Set(known.map(normalizeModel));
  const cited = circuits.flatMap((circuit) => circuit.compressors ?? []);

  return Array.from(new Set(cited.filter((model) => !available.has(normalizeModel(model)))));
};

export const problemOf = (error: unknown): Problem => {
  const response = (error as { response?: { status?: unknown; data?: unknown } } | null)?.response;
  const data =
    typeof response?.data === "object" && response.data !== null
      ? (response.data as { detail?: unknown; errors?: unknown })
      : null;
  const errors: Record<string, string[]> = {};

  if (data && typeof data.errors === "object" && data.errors !== null) {
    Object.entries(data.errors as Record<string, unknown>).forEach(([key, messages]) => {
      if (Array.isArray(messages)) {
        errors[key] = messages.map(String);
      }
    });
  }

  return {
    status: typeof response?.status === "number" ? response.status : null,
    detail: data && typeof data.detail === "string" ? data.detail : null,
    errors,
  };
};

export const failureMessages = (
  error: unknown,
  isField: (key: string) => boolean,
  t: Translate
): string[] => {
  const problem = problemOf(error);
  const messages = [
    ...(problem.detail ? [problem.detail] : []),
    ...Object.entries(problem.errors)
      .filter(([key]) => !isField(key))
      .flatMap(([, list]) => list),
  ];

  if (messages.length > 0 || Object.keys(problem.errors).length > 0) {
    return messages;
  }

  return [problem.status === 403 ? t("catalog.forbidden.body") : t("catalog.errors.generic")];
};

export const labelOf = (field: FieldSpec, t: Translate): string =>
  `${t(`catalog.fields.${field.name}`)}${field.unit ? ` (${field.unit})` : ""}${field.required ? " *" : ""}`;

export const nameOf = (lookups: Lookups, resource: ResourceName, id: unknown): string => {
  if (id === null || id === undefined) {
    return "—";
  }

  const record = lookups[resource]?.find((item) => item.id === id);

  return record ? String(record.fields[resourceSpec(resource).searchField]) : `#${id}`;
};

export const cellOf = (column: string, value: unknown, lookups: Lookups): string => {
  const reference = REFERENCES[column];

  if (reference) {
    return nameOf(lookups, reference, value);
  }

  if (value === null || value === undefined || value === "") {
    return "—";
  }

  return Array.isArray(value) ? value.join(", ") : String(value);
};

export const toFormValues = (fields: FieldSpec[], record: Fields | null): FormValues =>
  Object.fromEntries(
    fields.map((field) => {
      const value = record?.[field.name];

      if (value === null || value === undefined) {
        return [field.name, ""];
      }

      return [field.name, Array.isArray(value) ? value.join(", ") : String(value)];
    })
  );

const valueOf = (field: FieldSpec, raw: string): unknown => {
  const text = raw.trim();

  if (field.kind === "integers") {
    return parseVolts(text) ?? [];
  }

  if (field.kind === "number" || field.kind === "integer") {
    return text === "" ? null : Number(text);
  }

  return text === "" && field.nullable ? null : text;
};

export const toFields = (fields: FieldSpec[], values: FormValues): Fields =>
  Object.fromEntries(fields.map((field) => [field.name, valueOf(field, values[field.name] ?? "")]));

export const importMessages = (error: unknown, t: Translate): string[] => {
  const lines = Object.entries(problemOf(error).errors).flatMap(([key, messages]) =>
    messages.map((message) =>
      key === "csv" ? message : `${key.replace(/^fila_(\d+)\./, "fila $1, ")}: ${message}`
    )
  );

  return lines.length > 0 ? lines : failureMessages(error, () => false, t);
};
